let cloneDeep = require("lodash/cloneDeep");

import * as angular from "angular";
import {Link} from "../models/link.model";
import {StudyProgramsService} from "../common/study-programs/study-programs.service";
import {UserSessionService} from "../common/user-session/user-session.service";
import {TestprepRootScope} from "../models/testprep-rootscope.model";
import {SubscriptionStudyPrograms} from "../models/subscription-study-programs.model";
import {StudyProgram} from "../models/study-program.model";

const DEFAULT_SUBMISSION_TEXT = "Save AP Subjects";
const SAVING_SUBMISSION_TEXT = "Saving AP Subjects ...";

interface AssignRouteLocals {
    subscriptionStudyPrograms: SubscriptionStudyPrograms;
    [index: string]: any;
    $scope: angular.IScope;
    $template: string;
}

export class SetApProgramsController {
    crumbs: Link[];
    tenantId: string;
    district: boolean;
    submittingPrograms: boolean = false;
    selectedPrograms: String[] = [];
    activeStudyPrograms: StudyProgram[] = [];
    pendingStudyPrograms: StudyProgram[] = [];
    subscriptionStudyPrograms: SubscriptionStudyPrograms;

    /* @ngInject */
    constructor(private $route: angular.route.IRouteService,
                private userSessionService: UserSessionService,
                private studyProgramsService: StudyProgramsService,
                private $rootScope: TestprepRootScope, 
                private $location: angular.ILocationService) {
    }

    $onInit(): void {
        this.district = this.userSessionService.isDistrictMode();
        if (this.district) {
            this.tenantId = this.userSessionService.getDistrictTenantId();
        } else {
            this.tenantId = this.$route.current.params.tenantId;
        }
        let locals = <AssignRouteLocals>this.$route.current.locals;
        this.subscriptionStudyPrograms = locals.subscriptionStudyPrograms;

        this.loadStudyPrograms();
        this.updateCrumbs();
    }

    isAllSubscriptionsActivated(): boolean {
        return this.activeStudyPrograms.length === this.subscriptionStudyPrograms.apCount;
    }
    
    getPrograms(): StudyProgram[] {
        return this.subscriptionStudyPrograms.studyPrograms;
    }
    
    getAbsUnselectedProgramCount(): number {
        return Math.abs(this.getUnselectedProgramCount());
    }

    getUnselectedProgramCount(): number {
        if(!this.subscriptionStudyPrograms.apCount) {
            return 0;
        }

        return this.subscriptionStudyPrograms.apCount - this.selectedPrograms.length;
    }
    
    areRemainingProgramsToBeSelected(): boolean {
        return this.getUnselectedProgramCount() > 0;
    }

    isUnselectedPrograms(count: number): boolean {
        return this.getUnselectedProgramCount() === count;
    }

    moreProgramsToBeSelected(count: number): boolean {
        return this.getUnselectedProgramCount() < count;
    }
    
    isDisabledProgram(courseSlug: String): boolean {
        if(this.submittingPrograms || !courseSlug || this.isAllSubscriptionsActivated()) {
            return true;
        }

        if(this.getUnselectedProgramCount() > 0) {
            return false;
        }

        return !this.isSelectedProgram(courseSlug);
    }
    
    isSelectedProgram(courseSlug: String): boolean {
        return courseSlug && this.selectedPrograms.indexOf(courseSlug) >= 0;
    }

    toggleProgram(courseSlug: String): boolean {
        if(!courseSlug || this.isAllSubscriptionsActivated()) {
            return false;
        }

        let selected = false;

        let selectedProgramIndex = this.selectedPrograms.indexOf(courseSlug);
        if (selectedProgramIndex >= 0) {
            this.selectedPrograms.splice(selectedProgramIndex, 1);
        } else if(this.getUnselectedProgramCount() > 0) {
            this.selectedPrograms.push(courseSlug);
            selected = true;
        }

        return selected;
    };

    goHome = (): void => {
        if (this.userSessionService.isDistrictMode()) {
            this.$location.path(`/${this.userSessionService.getDistrictTenantId()}`);
        } else {
            this.$location.path(`/${this.tenantId}`);
        }
    };

    submitReady(): boolean {
        return this.selectedPrograms.length === this.subscriptionStudyPrograms.apCount;
    }

    getSubmissionText(): String {
        return (this.submittingPrograms) ? SAVING_SUBMISSION_TEXT : DEFAULT_SUBMISSION_TEXT;
    }

    submit(): void {
        if(!this.submittingPrograms) {
            this.$rootScope.loadingError = false;

            if(!this.submitReady()) {
                this.$rootScope.loadingError = true;
            } else {
                this.submittingPrograms = true;
                this.pendingStudyPrograms = [];
                for (let studyProgram of this.subscriptionStudyPrograms.studyPrograms) {
                    let pendingStudyProgram = cloneDeep(studyProgram);
                    this.pendingStudyPrograms.push(pendingStudyProgram);
                    pendingStudyProgram.subscribed = this.selectedPrograms.indexOf(pendingStudyProgram.courseSlug) >= 0;
                }

                this.studyProgramsService.updateStudyProgramsSubscription(this.pendingStudyPrograms,
                    this.subscriptionStudyPrograms.apCount, this.tenantId)
                    .then(this.successCallback, this.errorCallback);
            }
        }
    }

    private errorCallback = () => {
        this.pendingStudyPrograms = [];
        this.submittingPrograms = false;
        this.$rootScope.loadingError = true;
    };

    private successCallback = (status: number) => {
        if (status >= 400) {
            this.errorCallback();
        } else {
            this.subscriptionStudyPrograms.studyPrograms = this.pendingStudyPrograms;
            this.pendingStudyPrograms = [];
            this.loadStudyPrograms();

            this.submittingPrograms = false;
        }
    };

    private loadStudyPrograms() {
        if(this.subscriptionStudyPrograms.apCount > 0) {
            this.activeStudyPrograms = this.subscriptionStudyPrograms.studyPrograms.filter(function(studyProgram) {
                return studyProgram.subscribed;
            });

            if (this.subscriptionStudyPrograms.apCount === this.subscriptionStudyPrograms.studyPrograms.length) {
                // Default all Study Programs to selected
                for (let studyProgram of this.subscriptionStudyPrograms.studyPrograms) {
                    this.selectedPrograms.push(studyProgram.courseSlug);
                }
            }
        }
    }

    private updateCrumbs() {
        if (this.district) {
            this.crumbs = [
                { name: "Naviance Test Prep: District", url: `${this.tenantId}` },
                { name: "Setup AP Study Programs" }
            ];
        } else {
            this.crumbs = [
                { name: "Naviance Test Prep", url: `${this.tenantId}` },
                { name: "Setup AP Study Programs" }
            ];
        }
    }
}


/** WEBPACK FOOTER **
 ** ./~/tslint-loader!./app/set-ap-programs/set-ap-programs.controller.ts
 **/